import { c as _c } from "react/compiler-runtime";
import type { LiveItem } from '@module/apis/client-side/live/type';
import SettingConfig from '@module/config/setting';
import { aspCond } from '@module/helpers/conditions/condition';
import getSpecialDayLottieFileName from '@module/helpers/lottie/getSpecialDayLottieFileName';
import { scrollIntoPlayerArea } from '@module/helpers/play/scrollIntoPlayerArea';
import useBroadcastDescriptionResizeHandler from '@module/helpers/useBroadcastDescriptionResizeHandler';
import { cn } from '@module/lib/utils';
import { useASPInfo } from '@module/queries/asp/query';
import useChatPanelStore from '@module/stores/useChatPanelStore';
import useIsDarkModeStore from '@module/stores/useIsDarkModeStore';
import { formatTimeElapsedSince } from '@module/utils/date/convertDateFormat';
import formatNumberWithComma from '@module/utils/formatNumberWithComma';
import useIsMounted from '@module/utils/useIsMounted';
import BaseSVG from '@pc/components/image/BaseSVG';
import BaseLottie from '@pc/components/lottie/BaseLottie';
import { TooltipBox } from '@pc/components/tooltip/TooltipBox';
import useGoPlay from '@pc/hooks/play/useGoPlay';
import { useParams, usePathname, useRouter } from 'next/navigation';
import { useCallback, useState } from 'react';
interface Props {
  item: LiveItem;
}
function BroadcastUserItem(t0) {
  const $ = _c(120);
  const {
    item
  } = t0;
  const {
    title,
    user,
    isPw,
    fanLevel,
    userId: streamerId,
    userNick,
    bjAnni,
    isAdult,
    listDeco: t1,
    type,
    startTime,
    listUp,
    userLimit,
    globalYN,
    latencyMode
  } = item;
  const listDeco = t1 === undefined ? "0" : t1;
  const thumbUrl = item.thumbUrl ?? item.ivsThumbnail;
  const [tooltipOpen, setTooltipOpen] = useState(false);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      onOverThreshold: () => setTooltipOpen(true),
      onUnderThreshold: () => setTooltipOpen(false)
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    observerRef
  } = useBroadcastDescriptionResizeHandler(t2);
  const pathname = usePathname();
  const isPlayPage = pathname.includes("/play");
  const isLivePage = pathname.includes("/live");
  const isListUpIcon = isPlayPage || isLivePage;
  const isSpecialDay = !!bjAnni;
  const router = useRouter();
  const rawParams = useParams();
  const userId = rawParams?.userId ? decodeURIComponent(rawParams.userId) : rawParams?.userId;
  const isMounted = useIsMounted();
  const {
    isChatPanelOpen
  } = useChatPanelStore();
  const {
    isDark
  } = useIsDarkModeStore();
  const {
    goPlay
  } = useGoPlay();
  let t3;
  if ($[1] !== isDark) {
    t3 = url => {
      if (url.includes("_adult")) {
        return `${SettingConfig.S3_BUCKET_URL}/img/bg/silver_adult${isDark ? "_dark" : ""}.png`;
      }
      return url;
    };
    $[1] = isDark;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const getThumbUrl = t3;
  const isFull = user >= userLimit;
  const titleLengthCheck = _temp;
  const {
    data: aspInfo
  } = useASPInfo();
  const isAspInfo = !!aspInfo;
  const t4 = cn("w-1/4 xxxl:w-[16.5%] pr-[20px] pb-[28px] lg:w-1/5 xl:w-1/6", {
    "w-1/3 lg:w-1/4 xl:w-1/5": isPlayPage && isChatPanelOpen && !isAspInfo
  });
  const t5 = "break-all";
  const t6 = "group relative block overflow-hidden bg-center bg-cover bg-no-repeat hover:cursor-pointer";
  let t7;
  if ($[3] !== goPlay || $[4] !== item || $[5] !== streamerId || $[6] !== userId) {
    t7 = e => {
      e.stopPropagation();
      if (streamerId === userId) {
        scrollIntoPlayerArea();
      } else {
        goPlay(item);
      }
    };
    $[3] = goPlay;
    $[4] = item;
    $[5] = streamerId;
    $[6] = userId;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== latencyMode) {
    t8 = latencyMode === "RT" && <BaseSVG src={`${SettingConfig.S3_BUCKET_URL}/img/icon/live_plus.svg`} width={70} height={24} className="pointer-events-none absolute top-[8px] left-[8px] z-10" />;
    $[8] = latencyMode;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = cn("inset-0 top-0 left-0 aspect-video h-max w-full object-cover transition-transform duration-300 group-hover:scale-110");
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== getThumbUrl || $[12] !== thumbUrl) {
    t10 = getThumbUrl(thumbUrl);
    $[11] = getThumbUrl;
    $[12] = thumbUrl;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== item.userImg) {
    t11 = e_0 => {
      if (e_0.currentTarget.src === item.userImg) {
        return;
      }
      e_0.currentTarget.src = item.userImg;
    };
    $[14] = item.userImg;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t10 || $[17] !== t11 || $[18] !== title) {
    t12 = <img width="100%" height="100%" className={t9} src={t10} onError={t11} alt={title} />;
    $[16] = t10;
    $[17] = t11;
    $[18] = title;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== t12 || $[21] !== t8) {
    t13 = <div className="relative overflow-hidden rounded-[8px] ">{t8}{t12}</div>;
    $[20] = t12;
    $[21] = t8;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== isSpecialDay) {
    t14 = isSpecialDay && <div className="absolute inset-0 top-0 left-0 aspect-video h-max w-full rounded-[8px] bg-black bg-opacity-50" />;
    $[23] = isSpecialDay;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== bjAnni || $[26] !== isSpecialDay) {
    t15 = isSpecialDay && <BaseLottie src={`${SettingConfig.S3_BUCKET_URL}/json/lottie/anniversary/${getSpecialDayLottieFileName(bjAnni)}`} className={cn("-translate-x-1/2 absolute top-0 left-1/2 z-[1] h-[calc(100%-58px)] transition-opacity ")} />;
    $[25] = bjAnni;
    $[26] = isSpecialDay;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  const T0 = TooltipBox;
  const t16 = title || "";
  let t17;
  if ($[28] !== t16 || $[29] !== titleLengthCheck) {
    t17 = titleLengthCheck(t16);
    $[28] = t16;
    $[29] = titleLengthCheck;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  const t18 = !tooltipOpen;
  const t19 = "start";
  const t20 = `bg-listDeco-${listDeco}`;
  let t21;
  if ($[31] !== t20) {
    t21 = cn("video_card_wrapper flex h-[80px] w-full bg-[length:auto_100%] bg-center bg-repeat-x pt-[10px]", t20);
    $[31] = t20;
    $[32] = t21;
  } else {
    t21 = $[32];
  }
  const t22 = "min-w-0 flex-1";
  const t23 = "mt-0 w-full align-top";
  const t24 = cn("relative line-clamp-2 text-colorFg19", {
    "pr-[18px]": isListUpIcon
  });
  let t25;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <span />;
    $[33] = t25;
  } else {
    t25 = $[33];
  }
  let t26;
  if ($[34] !== isAdult) {
    t26 = isAdult && <img src={`${SettingConfig.S3_BUCKET_URL}/img/icon/ico_19.png`} width={20} alt="19" className="mr-1 mb-1 inline align-middle" />;
    $[34] = isAdult;
    $[35] = t26;
  } else {
    t26 = $[35];
  }
  let t27;
  if ($[36] !== isPw) {
    t27 = isPw && <img src={`${SettingConfig.S3_BUCKET_URL}/img/icon/ico_lock.png`} width={15} alt="lock" className="mr-1 mb-1 inline align-middle" />;
    $[36] = isPw;
    $[37] = t27;
  } else {
    t27 = $[37];
  }
  let t28;
  if ($[38] !== item.browser) {
    t28 = item.browser === "android" && <img src={`${SettingConfig.S3_BUCKET_URL}/img/icon/ico_mobile.png`} width={16} alt="mobile" className="mr-1 mb-1 inline align-middle" />;
    $[38] = item.browser;
    $[39] = t28;
  } else {
    t28 = $[39];
  }
  let t29;
  if ($[40] !== type) {
    t29 = type === "charge" && <img src={`${SettingConfig.S3_BUCKET_URL}/img/icon/ico_money.png`} width={16} alt="charge" className="mr-1 mb-1 inline align-middle" />;
    $[40] = type;
    $[41] = t29;
  } else {
    t29 = $[41];
  }
  let t30;
  if ($[42] !== fanLevel || $[43] !== type) {
    t30 = type === "fan" && <img src={`${SettingConfig.S3_BUCKET_URL}/img/grade/grade_${fanLevel}.svg`} width={20} alt="fan" className="mr-1 mb-1 inline align-middle" />;
    $[42] = fanLevel;
    $[43] = type;
    $[44] = t30;
  } else {
    t30 = $[44];
  }
  let t31;
  if ($[45] !== t26 || $[46] !== t27 || $[47] !== t28 || $[48] !== t29 || $[49] !== t30) {
    t31 = <span>{t26}{t27}{t28}{t29}{t30}</span>;
    $[45] = t26;
    $[46] = t27;
    $[47] = t28;
    $[48] = t29;
    $[49] = t30;
    $[50] = t31;
  } else {
    t31 = $[50];
  }
  const t32 = title || "";
  let t33;
  if ($[51] !== t32 || $[52] !== titleLengthCheck) {
    t33 = titleLengthCheck(t32);
    $[51] = t32;
    $[52] = titleLengthCheck;
    $[53] = t33;
  } else {
    t33 = $[53];
  }
  let t34;
  if ($[54] !== t33) {
    t34 = <span className="text-colorFg01">{t33}</span>;
    $[54] = t33;
    $[55] = t34;
  } else {
    t34 = $[55];
  }
  let t35;
  if ($[56] !== isListUpIcon || $[57] !== listUp) {
    t35 = isListUpIcon && listUp && <img className="absolute top-[4px] right-0" src={`${SettingConfig.S3_BUCKET_URL}/img/store/item/listUp/${listUp}.png`} width={18} height={18} alt="listup" />;
    $[56] = isListUpIcon;
    $[57] = listUp;
    $[58] = t35;
  } else {
    t35 = $[58];
  }
  let t36;
  if ($[59] !== observerRef || $[60] !== t24 || $[61] !== t31 || $[62] !== t34 || $[63] !== t35) {
    t36 = <p ref={observerRef} className={t24}>{t25}{t31}{t34}{t35}</p>;
    $[59] = observerRef;
    $[60] = t24;
    $[61] = t31;
    $[62] = t34;
    $[63] = t35;
    $[64] = t36;
  } else {
    t36 = $[64];
  }
  let t37;
  if ($[65] !== globalYN) {
    t37 = !SettingConfig.isProd && globalYN === "Y" && <img className="mr-1 inline w-[60px] align-middle" src={`${SettingConfig.S3_BUCKET_URL}/img/vipDeco/ico_global_tag.svg`} alt="global" />;
    $[65] = globalYN;
    $[66] = t37;
  } else {
    t37 = $[66];
  }
  let t38;
  if ($[67] !== item.newBjYN) {
    t38 = item.newBjYN === "Y" && <img className="mr-1 inline w-[14px] align-middle" src={`${SettingConfig.S3_BUCKET_URL}/img/icon/new_round.png`} alt="viewer" />;
    $[67] = item.newBjYN;
    $[68] = t38;
  } else {
    t38 = $[68];
  }
  let t39;
  if ($[69] !== router || $[70] !== streamerId) {
    t39 = e_1 => {
      e_1.stopPropagation();
      if (aspCond.if("iframe-asp")) {
        return;
      }
      router.push(`/channel/${streamerId}/notice/`);
    };
    $[69] = router;
    $[70] = streamerId;
    $[71] = t39;
  } else {
    t39 = $[71];
  }
  let t40;
  if ($[72] !== t39 || $[73] !== userNick) {
    t40 = <button onClick={t39} className="inline align-middle text-[13px] hover:underline">{userNick}</button>;
    $[72] = t39;
    $[73] = userNick;
    $[74] = t40;
  } else {
    t40 = $[74];
  }
  let t41;
  let t42;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <div className="mx-[2px] inline items-center justify-center align-middle text-sm">·</div>;
    t42 = <img className="mr-1 inline h-[11px] align-middle" src={`${SettingConfig.S3_BUCKET_URL}/img/icon/viewer_black_line.png`} alt="viewer" />;
    $[75] = t41;
    $[76] = t42;
  } else {
    t41 = $[75];
    t42 = $[76];
  }
  let t43;
  if ($[77] !== isFull || $[78] !== user) {
    t43 = isFull ? "FULL" : formatNumberWithComma(user);
    $[77] = isFull;
    $[78] = user;
    $[79] = t43;
  } else {
    t43 = $[79];
  }
  let t44;
  if ($[80] !== t43) {
    t44 = <span className="inline align-middle">{t43}</span>;
    $[80] = t43;
    $[81] = t44;
  } else {
    t44 = $[81];
  }
  let t45;
  let t46;
  if ($[82] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <div className="mx-[2px] inline items-center justify-center align-middle text-sm">·</div>;
    t46 = <img className="mr-1 inline h-[11px] align-middle" src={`${SettingConfig.S3_BUCKET_URL}/img/icon/ico_list_look.png`} alt="look" />;
    $[82] = t45;
    $[83] = t46;
  } else {
    t45 = $[82];
    t46 = $[83];
  }
  let t47;
  if ($[84] !== item.playCnt) {
    t47 = formatNumberWithComma(item.playCnt);
    $[84] = item.playCnt;
    $[85] = t47;
  } else {
    t47 = $[85];
  }
  let t48;
  if ($[86] !== t47) {
    t48 = <span className="inline align-middle">{t47}</span>;
    $[86] = t47;
    $[87] = t48;
  } else {
    t48 = $[87];
  }
  let t49;
  let t50;
  if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = <div className="mx-[5px] inline items-center justify-center align-middle text-sm">·</div>;
    t50 = <img className="mr-1 inline h-[11px] align-middle" src={`${SettingConfig.S3_BUCKET_URL}/img/icon/ico_list_time.png`} alt="time" />;
    $[88] = t49;
    $[89] = t50;
  } else {
    t49 = $[88];
    t50 = $[89];
  }
  let t51;
  if ($[90] !== isMounted || $[91] !== startTime) {
    t51 = isMounted && <span className="align-middle">{formatTimeElapsedSince(startTime)}</span>;
    $[90] = isMounted;
    $[91] = startTime;
    $[92] = t51;
  } else {
    t51 = $[92];
  }
  let t52;
  if ($[93] !== t37 || $[94] !== t38 || $[95] !== t40 || $[96] !== t44 || $[97] !== t48 || $[98] !== t51) {
    t52 = <div className="block w-full overflow-hidden overflow-ellipsis whitespace-nowrap align-top text-[#777] text-[13px]">{t37}{t38}{t40}{t41}{t42}{t44}{t45}{t46}{t48}{t49}{t50}{t51}</div>;
    $[93] = t37;
    $[94] = t38;
    $[95] = t40;
    $[96] = t44;
    $[97] = t48;
    $[98] = t51;
    $[99] = t52;
  } else {
    t52 = $[99];
  }
  let t53;
  if ($[100] !== t36 || $[101] !== t52) {
    t53 = <div className={t22}><div className={t23}>{t36}{t52}</div></div>;
    $[100] = t36;
    $[101] = t52;
    $[102] = t53;
  } else {
    t53 = $[102];
  }
  let t54;
  if ($[103] !== t21 || $[104] !== t53) {
    t54 = <div className={t21}>{t53}</div>;
    $[103] = t21;
    $[104] = t53;
    $[105] = t54;
  } else {
    t54 = $[105];
  }
  let t55;
  if ($[106] !== T0 || $[107] !== t17 || $[108] !== t18 || $[109] !== t54) {
    t55 = <T0 content={t17} disabled={t18} align={t19}>{t54}</T0>;
    $[106] = T0;
    $[107] = t17;
    $[108] = t18;
    $[109] = t54;
    $[110] = t55;
  } else {
    t55 = $[110];
  }
  let t56;
  if ($[111] !== t13 || $[112] !== t14 || $[113] !== t15 || $[114] !== t55 || $[115] !== t7) {
    t56 = <div className={t5}><div className={t6} onClick={t7}>{t13}{t14}{t15}{t55}</div></div>;
    $[111] = t13;
    $[112] = t14;
    $[113] = t15;
    $[114] = t55;
    $[115] = t7;
    $[116] = t56;
  } else {
    t56 = $[116];
  }
  let t57;
  if ($[117] !== t4 || $[118] !== t56) {
    t57 = <li className={t4}>{t56}</li>;
    $[117] = t4;
    $[118] = t56;
    $[119] = t57;
  } else {
    t57 = $[119];
  }
  return t57;
}
function _temp(title_0) {
  let replaceTitle = title_0 || "";
  if (replaceTitle.length > 38) {
    replaceTitle.substring(0, 38);
    replaceTitle = `${replaceTitle.substring(0, 38)}\n${replaceTitle.substring(38)}`;
  }
  return replaceTitle;
}
export default BroadcastUserItem;